import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";

const SCRIPTS = [
  { id: "royal-serif", label: "Royal Serif", font: "font-serif italic tracking-wide", desc: "Classic Roman elegance" },
  { id: "cursive-script", label: "Calligraphy Script", font: "font-cursive font-medium tracking-normal", desc: "Romantic handwritten flourish" },
  { id: "modern-sans", label: "Modern Minimal", font: "font-sans uppercase font-bold tracking-widest", desc: "Clean architectural geometry" },
  { id: "heritage-dev", label: "Devanagari / Indian", font: "font-serif font-bold tracking-tight", desc: "Traditional cultural script" },
];

const METALS = [
  { id: "gold-yellow", label: "18K Yellow Gold", bg: "from-[#F3DFA0] via-[#D4AF37] to-[#AA820A]", textColor: "text-[#664D00]", purity: "18K", metal: "gold" },
  { id: "gold-rose", label: "18K Rose Gold", bg: "from-[#F2C9B8] via-[#D89B85] to-[#B36B54]", textColor: "text-[#5C2B1C]", purity: "18K", metal: "gold" },
  { id: "silver-925", label: "925 Sterling Silver", bg: "from-[#F4F4F4] via-[#D9D9D9] to-[#999999]", textColor: "text-[#333333]", purity: "925", metal: "silver" },
];

export default function NameJewellery() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("Aarav");
  const [script, setScript] = useState(SCRIPTS[0].id);
  const [metalChoice, setMetalChoice] = useState(METALS[0].id);
  const [loaded, setLoaded] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products", { params: { limit: 24, personalisable: true } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const activeScript = SCRIPTS.find((s) => s.id === script) || SCRIPTS[0];
  const activeMetal = METALS.find((m) => m.id === metalChoice) || METALS[0];

  const quickOrder = (product) => {
    if (!name.trim()) return toast.error("Please enter a name first");
    const v = product.variants?.[0] || {
      id: "name-pendant-var",
      sku: `NAME-${product.slug}`,
      price_override: product.price_min || 32000,
      stock: 50,
      pricing: { subtotal: product.price_min || 32000, display_price: product.price_min || 32000, final: product.price_min || 32000 },
    };
    const pers = {
      selections: { Script: activeScript.label, Text: name.trim(), Finish: activeMetal.label },
      summary: `Engraving: "${name.trim()}" in ${activeScript.label} (${activeMetal.label})`,
      surcharge: 0,
    };
    addItem({ ...product, rate_info: { gst_rate: 3 } }, v, 1, pers);
    toast.success(`Personalised ${product.title} with "${name.trim()}" added to cart`);
    navigate("/cart");
  };

  return (
    <div className="container-x py-12 max-w-5xl" data-testid="name-jewellery-page">
      <div className="max-w-3xl">
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold">Name Jewellery</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">Wear the Name that Matters</h1>
        <p className="text-stone-600 mt-3 leading-relaxed">
          Custom cast and hand-engraved nameplates, bar pendants, and signet rings in solid 18K gold and 925 sterling silver.
          Type any name below to preview across our master artisan typography styles.
        </p>
      </div>

      {/* Live Artisan Typography & Metal Studio Preview */}
      <section className="mt-10 border border-gold/40 rounded-2xl bg-white p-6 sm:p-10 shadow-sm" data-testid="name-preview">
        <div className="max-w-lg mx-auto text-center">
          <label className="text-[11px] font-mono uppercase tracking-widest text-stone-500 block mb-2">Type Your Inscription</label>
          <input
            value={name}
            maxLength={14}
            onChange={(e) => setName(e.target.value)}
            className="text-center font-heading text-3xl sm:text-4xl font-semibold text-brand bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold w-full transition-colors"
            data-testid="name-preview-input"
            placeholder="Your name or word…"
            aria-label="Preview your name"
          />
        </div>

        {/* Script & Metal selectors */}
        <div className="grid sm:grid-cols-2 gap-6 mt-8 pt-6 border-t border-stone-100 max-w-2xl mx-auto">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-stone-500 mb-2">Artisan Script Style</p>
            <div className="space-y-1.5">
              {SCRIPTS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setScript(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-xs flex items-center justify-between transition-colors ${
                    script === s.id ? "border-brand bg-brand/5 font-semibold text-brand" : "border-stone-200 hover:border-stone-400 text-stone-600"
                  }`}
                  data-testid={`script-select-${s.id}`}
                >
                  <span>{s.label} <span className="text-[10px] text-stone-400 ml-1">({s.desc})</span></span>
                  {script === s.id && <Check size={14} className="text-brand" />}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-stone-500 mb-2">Precious Metal Finish</p>
            <div className="space-y-1.5">
              {METALS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMetalChoice(m.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-xs flex items-center justify-between transition-colors ${
                    metalChoice === m.id ? "border-brand bg-brand/5 font-semibold text-brand" : "border-stone-200 hover:border-stone-400 text-stone-600"
                  }`}
                  data-testid={`metal-select-${m.id}`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`h-3 w-3 rounded-full bg-gradient-to-br ${m.bg}`} />
                    {m.label}
                  </span>
                  {metalChoice === m.id && <Check size={14} className="text-brand" />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Realistic Plaque Simulation */}
        <div className="mt-10 p-10 rounded-2xl bg-[#FBF8F3] border border-stone-200 flex flex-col items-center justify-center min-h-[160px]">
          <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400 mb-4">Engraving Plaque Simulation</p>
          <div className={`px-10 py-5 rounded-xl bg-gradient-to-r ${activeMetal.bg} shadow-lg border border-white/40 flex items-center justify-center transform hover:scale-105 transition-transform duration-300`}>
            <span className={`text-3xl sm:text-4xl ${activeScript.font} ${activeMetal.textColor} drop-shadow-sm select-none`}>
              {name.trim() || "Siyaara"}
            </span>
          </div>
          <p className="text-[11px] text-stone-500 mt-4 font-mono">
            {activeMetal.label} · {activeScript.label}
          </p>
        </div>
      </section>

      {/* Personalisable Pieces Catalog */}
      <div className="mt-14">
        <h2 className="font-heading text-2xl font-semibold">Heirloom Pieces Ready to Inscribe</h2>
        <p className="text-sm text-stone-500 mt-1">Select any piece below to personalize with your previewed name.</p>
        {loaded && items.length === 0 && (
          <div className="p-8 border border-stone-200 bg-white rounded-xl mt-4 text-center">
            <p className="text-sm text-stone-600">Personalisable name pendants and rings can also be crafted on request.</p>
            <Link to="/corporate-gifting">
              <Button variant="outline" className="mt-3 rounded-full text-xs">Request Custom Inscription</Button>
            </Link>
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {items.map((p) => (
            <div key={p.id} className="border border-stone-200 bg-white p-4 rounded-xl hover:border-brand transition-all flex flex-col justify-between" data-testid={`name-product-${p.slug}`}>
              <Link to={`/products/${p.slug}`}>
                <div className="aspect-square bg-stone-50 mb-3 rounded overflow-hidden">
                  {(p.images || [])[0] ? (
                    <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-brand/30 font-heading text-2xl">S</div>
                  )}
                </div>
                <p className="text-xs font-medium truncate text-ink">{p.title}</p>
                <p className="text-xs font-mono text-gold font-semibold mt-0.5">{inr(p.price_min || 0)}</p>
              </Link>
              <Button
                size="sm"
                onClick={() => quickOrder(p)}
                className="w-full mt-3 bg-stone-900 hover:bg-brand text-white text-xs rounded-full h-8 btn-press"
                data-testid={`quick-order-${p.slug}`}
              >
                Inscribe "{name.trim() || "Name"}"
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
